import { platformSettingsSlice } from '@wellesley/settings/slices/platformSettingsSlice'
import { useDispatch, useSelector } from 'react-redux'
import { isEmpty } from 'lodash'
import { UploadFileProps } from '@wellesley/ui'
import { useRef, useEffect, useState } from 'react'

import {
  UploadItem,
  UploadStatus,
  UploadTag,
  UseUploadMediaProps,
} from '../../types'
import { useUploadFile } from '../useUploadFile'
import { getUploadType } from '../../helpers/getUploadType'
import {
  Upload,
  useDeleteApiV1UploadsByIdMutation,
  usePostApiV1UploadsByIdMetaMutation,
} from '@wellesley/openapi'
import { toast } from '@wellesley/system-toast'
import selectUploadFile from '../../helpers/selectUploadFile'
import usePreviewSize from '../usePreviewSize'
import getImageResolution from 'helpers/image/getImageResolution'
import {
  uploadsProgressSlice,
  UploadsProgressState,
} from '../../slice/progressSlice'
import getVideoInfo, { VideoInfo } from 'helpers/getVideoInfo'
import canPlayVideoBlob from '../../helpers/canPlayVideoBlob'
import genId from 'helpers/genId'
import { RootState } from '@wellesley/store'
import { isUpload } from '../../helpers/mapUpload'

import defaultThumbnail from '../../images/default_thumbnail.png'

export const MAX_UPLOADS = 16

const DEFAULT_MEDIA_INFO = {
  preview: defaultThumbnail,
  width: 400,
  height: 400,
}

const attachmentToUploadItem = (
  u: Upload,
  selectPreview: (u: Upload) => string,
): UploadItem => ({
  id: u.uploadId,
  meta: u.meta,
  type: u.uploadType,
  preview: selectPreview(u),
  status: UploadStatus.done,
  progress: 100,
  upload: new Promise((res) => res(u.uploadId)),
  cancel: () => {},
  duration: u.files[0].meta.duration,
  sizes: {
    width: u.files[0].meta.width,
    height: u.files[0].meta.height,
  },
})

export const useUploadMedia = ({
  feed,
  closeCb,
  resetOnClose,
  onMediaChange,
  previewSize: previewSizeFromProps,
}: UseUploadMediaProps) => {
  const videoExtensions = useSelector(
    platformSettingsSlice.selectors.selectVideoExtensions,
  )
  const previewSize = usePreviewSize(previewSizeFromProps)

  const uploadsLimit = useSelector(
    platformSettingsSlice.selectors.selectPostConfig,
  )?.max_media_attachments

  const selectPreview = (u: Upload) =>
    selectUploadFile({
      size: previewSize,
      upload: u,
      allowedExtensions: u.uploadType === 'VIDEO' ? videoExtensions : undefined,
    })?.uri || ''

  const uploads = useRef<UploadItem[]>(
    (feed?.uploads || [])
      .map((f) => attachmentToUploadItem(f, selectPreview))
      .filter((u) => !!u.preview),
  )

  const [deleteUpload] = useDeleteApiV1UploadsByIdMutation()

  if (closeCb && resetOnClose) {
    closeCb.current = () => {
      if (feed) return // if edit post, don't delete uploads

      for (const upload of uploads.current) {
        void (async () => {
          await deleteUpload({ id: await upload.upload })
        })()
      }
    }
  }

  const [mediaFiles, setMediaFiles] = useState<UploadFileProps[]>([])
  const dispatch = useDispatch()
  let rafId: number | null = null
  const pending: Record<string, number> = {}

  const objectUrlToIdRef = useRef<Record<string, string>>({})

  const uploadsProgress = useSelector(
    (state: RootState & { uploadsProgress?: UploadsProgressState }) =>
      (state.uploadsProgress as UploadsProgressState) ?? {},
  )

  const scheduleProgress = (id: string, percent: number) => {
    pending[id] = percent
    if (rafId != null) return
    rafId = requestAnimationFrame(() => {
      rafId = null
      const entries = Object.entries(pending)
      for (const [k, p] of entries) {
        dispatch(
          uploadsProgressSlice.actions.setProgress({ id: k, percent: p }),
        )
        delete pending[k]
      }
    })
  }

  const addMedia = (objects: UploadFileProps[]) => {
    if (mediaFiles.length + objects.length > uploadsLimit) {
      toast.error(`You can not upload more than ${uploadsLimit} files at once.`)
      return
    }

    setMediaFiles((files) => {
      const newList = [...files, ...objects]
      onMediaChange?.(newList.length)

      return newList
    })
  }

  const [updateMeta] = usePostApiV1UploadsByIdMetaMutation()

  const updateMedia = async (upload: Upload | UploadItem, altText: string) => {
    const isExistingUpload = isUpload(upload)

    if (isExistingUpload) {
      const uploadId = upload.uploadId
      const newMeta = { ...upload.meta, altText }

      await updateMeta({
        id: uploadId,
        body: {
          ...newMeta,
          altText,
        },
      }).unwrap()

      uploads.current = uploads.current.map((u) =>
        u.id === upload.uploadId ? { ...u, meta: newMeta } : u,
      )
    } else {
      const uploadId = await upload.upload
      const newMeta = { ...upload.meta, altText }

      await updateMeta({
        id: uploadId,
        body: {
          ...newMeta,
          altText,
        },
      }).unwrap()

      uploads.current = uploads.current.map((u) =>
        u.id === upload.id ? { ...u, meta: newMeta } : u,
      )
    }
  }

  const filterUpload = (id: string) => {
    const upload = uploads.current.find((u) => u.id === id)
    uploads.current = uploads.current.filter((m) => m.id !== id)

    const entry = Object.entries(objectUrlToIdRef.current).find(
      ([, v]) => v === id,
    )
    const objectUrl = entry?.[0] ?? upload?.preview

    setMediaFiles((files) => {
      const newList = objectUrl
        ? files.filter((f) => f.objectUrl !== objectUrl)
        : files
      onMediaChange?.(newList.length)

      return newList
    })

    if (objectUrl) delete objectUrlToIdRef.current[objectUrl]

    return upload
  }

  const removeMedia = async (id: string) => {
    const upload = filterUpload(id)

    if (upload?.status === UploadStatus.done) {
      void deleteUpload({ id: await upload.upload })
    } else if (upload?.status === UploadStatus.inprogress) {
      upload?.cancel()
    }
  }

  const [uploadFile, cancelUpload] = useUploadFile()

  const resetUploads = () => {
    uploads.current = []
    setMediaFiles([])
    objectUrlToIdRef.current = {}
  }

  useEffect(() => {
    const canStartUpload = mediaFiles.some((media) => {
      const sid = objectUrlToIdRef.current[media.objectUrl]
      return !uploads.current.some((u) => u.id === sid)
    })

    canStartUpload && void uploadMedia()
  }, [mediaFiles.length])

  const getIsError = (id: string) => uploadsProgress[id]?.error ?? false

  const uploadMedia = async () => {
    const oldUploads = uploads.current

    const newUploads = mediaFiles.map(async (media): Promise<UploadItem> => {
      const stableId = objectUrlToIdRef.current[media.objectUrl] ?? genId()
      objectUrlToIdRef.current[media.objectUrl] = stableId
      const isError = getIsError(stableId)

      const isExist = oldUploads.some((u) => u.id === stableId)
      if (isExist) return {} as UploadItem

      const onUpload = () => {
        scheduleProgress(stableId, 100)
      }

      const onProgress = (_loaded: number, _total: number, percent: number) => {
        scheduleProgress(stableId, percent)
      }

      const onError = (e: string) => {
        dispatch(
          uploadsProgressSlice.actions.setError({ id: stableId, error: e }),
        )
      }

      const canUseBlobVideo = canPlayVideoBlob(media.file)

      const type = getUploadType(media.file)
      const isVideo = type === 'VIDEO'
      const isImage = type === 'IMAGE'

      const info = isImage
        ? await getImageResolution(media.file)
        : isVideo
          ? canUseBlobVideo
            ? await getVideoInfo({ src: media.objectUrl })
            : DEFAULT_MEDIA_INFO
          : null

      const isUnrenderableImage = isImage && !info
      const preview = isVideo
        ? canUseBlobVideo
          ? media.objectUrl
          : DEFAULT_MEDIA_INFO.preview
        : isUnrenderableImage
          ? DEFAULT_MEDIA_INFO.preview
          : media.objectUrl

      return {
        id: stableId,
        meta: {
          name: media.file.name,
        },
        type,
        status: UploadStatus.inprogress,
        preview,
        progress: 0,
        cancel: cancelUpload,
        upload: isError
          ? Promise.resolve()
          : uploadFile({
              media,
              onError,
              onUpload,
              onProgress,
              tag: UploadTag.post,
            }),
        duration: (info as VideoInfo)?.duration,
        thumbnail: (info as VideoInfo)?.preview,
        sizes: {
          width: info?.width || DEFAULT_MEDIA_INFO.width,
          height: info?.height || DEFAULT_MEDIA_INFO.height,
        },
        isError: isError,
      } as UploadItem
    })

    const promises = await Promise.all(newUploads)

    uploads.current = [...oldUploads, ...promises.filter((u) => !isEmpty(u))]
  }

  return {
    resetUploads,
    uploads: uploads.current,
    addMedia,
    removeMedia,
    updateMedia,
  }
}
